import { Component, inject } from '@angular/core';
import { MediaBlock } from '../media-block/media-block';
import { InformationBlock } from '../information-block/information-block';
import { CreateContentService } from '../../services/create-content.service';
import { PublicationBlock } from '../publication-block/publication-block';
import {
  AbstractControl,
  FormControl,
  FormControlStatus,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { VisibilityType } from '../../types/visibility.type';
import { PlanningType } from '../../types/planning.type';
import { Coordinate } from '@kinomap-dev/ng-ui-lib/core';
import { Observable, Subscription } from 'rxjs';
import { SportType } from '../../types/sport.type';
import { scheduledValidator } from '../../utils/scheduled.validator';

@Component({
  selector: 'app-creation-gps-only-form',
  imports: [MediaBlock, InformationBlock, PublicationBlock, ReactiveFormsModule],
  templateUrl: './creation-gps-only-form.html',
  styleUrl: './creation-gps-only-form.scss',
})
export class CreationGpsOnlyForm {
  private createContentService: CreateContentService = inject(CreateContentService);

  gpsTrackForm: FormGroup = new FormGroup({
    mediaBlock: new FormGroup({
      coordinates: new FormControl<Coordinate[] | null>(null, [
        Validators.required,
        Validators.minLength(10),
      ]),
    }),
    informationBlock: new FormGroup({
      title: new FormControl<string | undefined>(undefined, [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(128),
      ]),
      sport: new FormControl<SportType | undefined>(undefined, [Validators.required]),
      description: new FormControl<string | undefined>(undefined, [Validators.maxLength(256)]),
      recordedWith: new FormControl<string | undefined>(undefined, [Validators.maxLength(256)]),
    }),
    publicationBlock: new FormGroup(
      {
        visibility: new FormControl<null | VisibilityType>('private', Validators.required),
        planning: new FormControl<null | PlanningType>('immediate', Validators.required),
        planningDate: new FormControl<Date | null>(null),
        planningTime: new FormControl<string | null>(null),
      },
      { validators: scheduledValidator },
    ),
  });
  subscriptions: Subscription[] = [];

  get mediaBlockFormGroup(): FormGroup {
    return this.gpsTrackForm.get('mediaBlock') as FormGroup;
  }

  get publicationBlockFormGroup(): FormGroup {
    return this.gpsTrackForm.get('publicationBlock') as FormGroup;
  }

  get informationBlockFormGroup(): FormGroup {
    return this.gpsTrackForm.get('informationBlock') as FormGroup;
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.gpsTrackForm.statusChanges.subscribe((status: FormControlStatus) => {
        if (status === 'VALID') {
          this.createContentService.gpsTrackFormIsValid.set(true);
        } else {
          this.createContentService.gpsTrackFormIsValid.set(false);
        }
      }),

      this.gpsTrackForm.get('informationBlock')!.valueChanges.subscribe((values) => {
        this.createContentService.title.set(values.title ?? undefined);
        this.createContentService.sport.set(values.sport ?? undefined);
        this.createContentService.description.set(values.description ?? '');
        this.createContentService.recordedWith.set(values.recordedWith ?? '');
      }),
      this.gpsTrackForm.get('publicationBlock')!.valueChanges.subscribe((values) => {
        this.createContentService.updatePublication(values);
      }),
    );

    const localSave: string | null = localStorage.getItem('gps-only_form');
    if (localSave) this.gpsTrackForm.patchValue(JSON.parse(localSave));
    this.createContentService.restoreForm(this.gpsTrackForm, 'gps-only');

    const pubBlock: AbstractControl = this.gpsTrackForm.get('publicationBlock')!;
    pubBlock.patchValue(
      { visibility: 'private', planning: 'immediate', planningDate: null, planningTime: null },
      { emitEvent: false },
    );
    this.createContentService.updatePublication(pubBlock.value);

    const infoBlock: AbstractControl = this.gpsTrackForm.get('informationBlock')!;
    this.createContentService.title.set(infoBlock.get('title')!.value ?? undefined);
    this.createContentService.sport.set(infoBlock.get('sport')!.value ?? undefined);
    this.createContentService.description.set(infoBlock.get('description')!.value ?? undefined);
    this.createContentService.recordedWith.set(infoBlock.get('recordedWith')!.value ?? undefined);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  markAllErrors(): void {
    this.gpsTrackForm.markAllAsTouched();
  }

  canDeactivate(): Observable<boolean> {
    return this.createContentService.canDeactivate(this.gpsTrackForm, 'gps-only');
  }
}
